import type { BillingInterval } from "../api/types";

/**
 * Free-trial rules for the two checkout intervals. The card is always collected
 * at checkout, but the first charge is deferred:
 *
 * - **Monthly** — a fixed 30-day free trial. First charge = signup + 30 days,
 *   then normal monthly renewal. This is a permanent behaviour.
 * - **Yearly** — a launch promo: free until the calendar new year, then billed
 *   for the solar year (1 Jan → 31 Dec, renewing every 1 Jan). The free window
 *   length varies with the signup date (subscribe in July → ~6 months free), so
 *   copy must NOT state a fixed number of days.
 *
 * ⚠️ The yearly promo has a HARDCODED end instant: {@link ANNUAL_TRIAL_PROMO_END}.
 * After it passes, the yearly plan is charged immediately with no gifted months —
 * `getCheckoutTrialInfo("yearly")` then reports `isTrial: false` and all the
 * promo-specific copy stops rendering. Revisit/remove this promo when that day
 * arrives.
 */

/** Days of free trial on the monthly plan (card taken now, first charge deferred). */
export const MONTHLY_TRIAL_DAYS = 30;

/**
 * End of the yearly "free until 1 January" launch promo, and the first-charge
 * date for anyone who subscribes to the yearly plan while it is active.
 * Local time — the billing new year is a calendar boundary, not a UTC one.
 */
export const ANNUAL_TRIAL_PROMO_END = new Date(2027, 0, 1, 0, 0, 0, 0);

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export type TrialInfo = {
  /** true when this checkout starts with a free period (nothing charged today). */
  isTrial: boolean;
  /** When the first charge lands, or null when charged immediately / no trial. */
  firstChargeAt: Date | null;
};

/**
 * Resolve the trial a checkout would start, given the interval and the current
 * time. Pure and time-injectable so callers (and tests) stay deterministic.
 */
export const getCheckoutTrialInfo = (
  interval: BillingInterval,
  now: Date = new Date(),
): TrialInfo => {
  if (interval === "yearly") {
    if (now.getTime() >= ANNUAL_TRIAL_PROMO_END.getTime()) {
      return { isTrial: false, firstChargeAt: null };
    }
    return { isTrial: true, firstChargeAt: ANNUAL_TRIAL_PROMO_END };
  }
  return {
    isTrial: true,
    firstChargeAt: new Date(now.getTime() + MONTHLY_TRIAL_DAYS * MS_PER_DAY),
  };
};
